//#region Libraries
import {
    useState, 
    useEffect } from "react";
//#endregion
//#region Styles
import {
    Container, 
    SearchTeacher, 
    TextInputTeacher, 
    SearchButtonTeacher 
} from "./styles";
import {
    Column, Row 
} from "components/styles";
//#endregion
//#region Components
import CustomTextInput from "components/CustomTextInput/CustomTextInput";
import CustomSelect from "components/CustomSelect/CustomSelect";
import SearchInDialogAlert from "components/SearchInDialogAlert/SearchInDialogAlert";
import OrDivider from "components/OrDivider/OrDivider";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import Preloader from "components/Preloader/Preloader";
//#endregion
//#region Services
import { requestGetTeachers } from "services/general";
//#endregion

const TeacherField = ({
    teacher, 
    changeField, 
    errors,
    errorTexts,
    showSearchTeacher,
    teacherAccounts,
    fillTeacherZoomAccountDetail
}) => {
    const { search, free } = teacher;
    //#region States
    const [showDialogSearchTeacher, setShowDialogSearchTeacher] = useState(false);
    const [loadingPreloaderSearchTeachers, setLoadingPreloaderSearchTeachers] = useState(false);
    const [timeoutIdToSearch, setTimeoutIdToSearch] = useState(null);
    const [matchedTeacherList, setMatchedTeacherList] = useState([]);
    const [mostSimilarTeacher, setMostSimilarTeacher] = useState("");
    //#endregion
    //#region Effects
    useEffect(() => {
        const { fullName, accountId } = free;
        if (search.id && (fullName || accountId > 0)) 
            changeField("search", { id: "", fullName: "" }, false);
    }, [free]);
    useEffect(() => {
        if (search.id) 
            changeField("free", {
                fullName: "",
                accountId: 0
            }, {
                fullName: false,
                accountId: false
            });
    }, [search.id]);
    //#endregion
    //#region Functions
    const toggleDialogSearchTeacher = (value = null) => {
        setShowDialogSearchTeacher(value !== null ? value : !showDialogSearchTeacher);
    }
    const changeSearchField = ({ id, text }) => {
        changeField("search", {
            id, 
            fullName: text
        }, false);
        fillTeacherZoomAccountDetail({
            teacher: {
                teacherId: id
            }
        });
    }
    const changeFreeField = (field, value, errorValue) => {
        changeField("free", {
            ...free, 
            [field]: value
        }, {
            ...errors.free,
            [field]: errorValue
        });
    }
    const changeFreeFullNameField = value => {
        changeFreeField("fullName", value, false);
    }
    const changeFreeAccountIdField = value => {
        changeFreeField("accountId", value, false);
        fillTeacherZoomAccountDetail({
            teacher: {
                teacherAccountId: value
            }
        });
    }
    const fillMatchedTeacherList = async nameTeacher => {
        // Borrando id del callback timeout si es que se teclea antes de la ejecución de la función
        clearTimeout(timeoutIdToSearch);
        // Programando ejecución de la búsqueda a 500ms
        const timeoutId = setTimeout(async () => {
            // Obteniendo referencia del elemento en focus
            const $input = document.activeElement;
            // Definiendo función de manejo del loader
            const nameTeacherTrim = nameTeacher.trim();
            const handlerLoading = (activateLoader) => {
                if (!nameTeacherTrim) 
                    return;
                $input[activateLoader ? "blur" : "focus"]();
                setLoadingPreloaderSearchTeachers(activateLoader);
            }
            handlerLoading(true);
            // Obteniendo lista de docentes
            const teacherList = !nameTeacherTrim ? [] : await getTeachers(nameTeacherTrim);
            handlerLoading(false);
            // Llenando de lista de docentes
            setMatchedTeacherList(teacherList);
            // Verificando si no fue encontrado para buscar los similares
            if (nameTeacherTrim && teacherList.length === 0) {
                handlerLoading(true);
                const teacherListMostSimilar = await getTeachers(nameTeacherTrim[0], true);
                handlerLoading(false);
                const newMostSimilar = teacherListMostSimilar.find((
                    { text }) => {
                    return text[0].toUpperCase() === nameTeacherTrim[0].toUpperCase();
                });
                setMostSimilarTeacher(newMostSimilar?.text || "");
            }
        }, 500);
        // Actualizando id del callback timeout
        setTimeoutIdToSearch(timeoutId);
    }
    const clearMatchedTeachersList = () => setMatchedTeacherList([]);
    const getTeachers = async (nameTeacher, similar) => {
        const data = await requestGetTeachers(nameTeacher, "Evento", similar);
        const fetchTeachers = data.map(({
            codigoDocente, nombreDocente
        }) => {
            return {
                id: codigoDocente,
                text: nombreDocente
            };
        });
        return fetchTeachers;
    }
    //#endregion
    const anyCompleted = errors.search && errors.free.fullName && errors.free.accountId;
    return (
        <Container>
            <Row className="flex-start">
                {showSearchTeacher && 
                    <SearchTeacher>
                        <SearchButtonTeacher>
                            <h4>
                                Puedes buscar al docente
                            </h4>
                            <SearchInDialogAlert
                                widthFoundField="300px"
                                buttonProps={{
                                    text: "Buscar docente", 
                                    disabled: free.fullName,
                                    variant: "secondary",
                                    error: errors.search,
                                    errorText: anyCompleted ? null : errorTexts.teacherSearch
                                }}
                                placeholder="Escribe el nombre y apellido"
                                label="Busca al docente dando click en el botón"
                                changeSearchField={changeSearchField}
                                data={matchedTeacherList}
                                fillData={fillMatchedTeacherList}
                                clearData={clearMatchedTeachersList}
                                mostSimilar={mostSimilarTeacher}
                                firstSelection={search.id}
                                name={search.fullName}
                                titleDialog="Buscar al docente"
                                loadingSearch={loadingPreloaderSearchTeachers}
                                showDialogAlert={showDialogSearchTeacher}
                                toggleDialogAlert={toggleDialogSearchTeacher}
                                notFoundMessage="El nombre que estás buscando no está en la lista de docentes."
                                notFoundSubmessage="*Si el nombre no está en la lista, puedes escribirlo manualmente."/>
                                <Preloader 
                                    message="Buscando docentes..."
                                    hidden={!loadingPreloaderSearchTeachers}/>
                        </SearchButtonTeacher>
                        <OrDivider height={errors.search ? "130px" : "120px"}/>
                    </SearchTeacher>
                }
                <Column gap="6px" width="unset">
                    <h4>
                        Puedes escribir sus datos
                    </h4>
                    <TextInputTeacher>
                        <CustomTextInput
                            label="Escribe sus datos si no está en la lista"
                            value={free.fullName}
                            onChange={changeFreeFullNameField}
                            placeholder="Nombres y Apellidos"
                            error={errors.free.fullName}
                            errorText={anyCompleted 
                                ? null 
                                : "Agrega el docente que estará a cargo"}/>
                        <CustomSelect
                            label="Elige la cuenta que utilizará el docente"
                            value={free.accountId}
                            onChange={changeFreeAccountIdField}
                            helperText="*Con esta cuenta, se creará la sesión de Zoom."
                            disableOptionText="Selecciona una cuenta"
                            optionsProps={
                                teacherAccounts.map(({ 
                                    id, email
                                }) => {
                                    return {
                                        value: id,
                                        text: email 
                                    };
                                })}
                            error={errors.free.accountId}
                            errorText={anyCompleted ? null : errorTexts.teacherFreeAccount}
                            width={{
                                containerSelect: "400px"
                            }}/>
                    </TextInputTeacher>
                </Column>
            </Row>
            <ErrorMessage
                error={anyCompleted}
                text={`${showSearchTeacher ? "Agrega o escribe" : "Escribe"} el nombre del docente a cargo`}/>
        </Container>
    );
}

export default TeacherField;